import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationCancel } from '@angular/router';
import { Constants } from '../constants';
import { StorageService } from './storage.service';

// GA4 only
declare let gtag: any;

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {

  // ============================================================
  // Known search engines whose UTM/referrer source values should
  // be normalized to a bare engine name (no scheme, no www., no
  // ccTLD/subdomain), e.g. "www.google.co.uk" -> "google",
  // "m.google.de" -> "google". Extend this list for any other
  // platform that shows the same www./subdomain leak.
  // ============================================================
  private readonly knownSearchEngines = [
    'google',
    'bing',
    'yahoo',
    'duckduckgo',
    'yandex',
    'baidu'
  ];

  constructor(
    private storageService: StorageService,
    @Inject(PLATFORM_ID) private platform: any
  ) { }

  // ============================================================
  // Generic Event Tracking
  // ============================================================
  public emitEvent(
    eventName: string,
    eventCategoryValue: string,
    eventActionValue: string,
    eventLabelValue?: string,
    eventValueValue?: number
  ) {

    try {

      gtag('event', eventName, {
        event_category: eventCategoryValue,
        event_label: eventLabelValue,
        event_action: eventActionValue,
        value: eventValueValue
      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Navigation Timing Event
  // ============================================================
  public emitGTagEvent(
    eventName: string,
    navigationName: string,
    eventValue: string
  ) {

    try {

      gtag('event', eventName, {
        name: navigationName,
        value: eventValue
      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // SPA Page View Tracking
  // Single source of truth for ALL page_view events (initial +
  // every Angular route change). Called by base.component on
  // NavigationEnd. Reattaches stored campaign attribution on
  // every hit so source/medium never drops on SPA navigations.
  // ============================================================
  public emitPageBrowseActivity(
    _eventName: string,
    urlAfterRedirects: string
  ) {

    if (!isPlatformBrowser(this.platform)) {
      return;
    }

    try {

      // Re-attach stored attribution so every SPA page_view carries
      // the original landing source/medium — not just the first hit.
      const storedAttribution =
        this.storageService.sessionGetItem('ga4_utm_params') || {};

      const campaignFields: any = {};

      if (storedAttribution.utm_source) {
        campaignFields.campaign_source = storedAttribution.utm_source;
      }
      if (storedAttribution.utm_medium) {
        campaignFields.campaign_medium = storedAttribution.utm_medium;
      }
      if (storedAttribution.utm_campaign) {
        campaignFields.campaign_name = storedAttribution.utm_campaign;
      }
      if (storedAttribution.utm_term) {
        campaignFields.campaign_term = storedAttribution.utm_term;
      }
      if (storedAttribution.utm_content) {
        campaignFields.campaign_content = storedAttribution.utm_content;
      }

      gtag('event', 'page_view', Object.assign({}, campaignFields, {

        page_title: document.title,

        page_location:
          window.location.origin + urlAfterRedirects,

        page_path: urlAfterRedirects

      }));

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // emitPagePathActivity kept as alias to avoid breaking other call sites.
  public emitPagePathActivity(urlAfterRedirects: string): void {
    this.emitPageBrowseActivity('page', urlAfterRedirects);
  }

  // ============================================================
  // Purchase Tracking
  // ============================================================
  public emitPurchaseEventTracking(
    eventName: string,
    orderData: any
  ) {

    const itemsArray = [];

    let i = 0;

    orderData.skus.forEach(element => {

      i++;

      itemsArray.push({

        item_id: element.product.id,

        item_name: element.product.name,

        item_category: element.product.subcategory,

        item_variant: element.id,

        index: i,

        quantity: element.quantity,

        price: element.sellPrice

      });

    });

    try {

      gtag('event', eventName, {

        transaction_id: orderData.id,

        affiliation: 'Jamestown Distributors',

        value: orderData.orderTotal,

        currency: 'USD',

        tax: orderData.salesTax,

        shipping:
          orderData.shipping.total +
          orderData.shipping.hazmatFee +
          orderData.shipping.rushOrderFee,

        coupon:
          orderData.promoCode || '',

        items: itemsArray

      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

    this.emitOrderConversionTracking(
      'conversion',
      orderData
    );

  }

  // ============================================================
  // Google Ads Conversion
  // ============================================================
  public emitOrderConversionTracking(
    eventName: string,
    orderData: any
  ) {

    const subTotal =
      orderData.skus
        .map(product => product.quantity * product.sellPrice)
        .reduce((a, b) => a + b);

    try {

      gtag('event', eventName, {

        send_to: 'AW-1072569469/zOu8CMT7PhD9uLj_Aw',

        value: subTotal,

        currency: Constants.currencyUSD,

        transaction_id: orderData.id

      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Product Detail Views
  // ============================================================
  public emitProductDetailViews(
    productData: any,
    skuData: any
  ) {

    try {

      gtag('event', 'view_item', {

        items: [
          {
            item_id: productData.id,

            item_name: productData.name,

            item_brand:
              productData.brand?.name || '',

            item_category:
              `${productData.breadcrumbs?.category || ''}/${productData.breadcrumbs?.subcategory || ''}`,

            item_variant: skuData.name,

            price: skuData.price.sellPrice
          }
        ]

      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Product List Impression
  // ============================================================
  public emitProductImpressionTracking(
    productList: any
  ) {

    const itemsArray = [];

    let i = 0;

    productList.forEach(element => {

      i++;

      itemsArray.push({

        item_id: element.uid,

        item_name: element.name,

        item_brand: element.brand,

        item_category:
          element.ss_category
            ? element.ss_category[0].replace('&gt;', '/')
            : '',

        item_variant: element.sku,

        index: i,

        price: element.ss_price

      });

    });

    try {

      gtag('event', 'view_item_list', {
        items: itemsArray
      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Checkout Tracking
  // ============================================================
  public emitCheckoutEventTracking(
    eventName: string,
    checkoutData: any,
    orderObj: any
  ) {

    const itemsArray = [];

    let i = 0;

    checkoutData.skus.forEach(element => {

      i++;

      itemsArray.push({

        item_id: element.product.id,

        item_name: element.product.name,

        item_category:
          `${element.product.category || ''}/${element.product.subcategory || ''}`,

        item_variant: element.id,

        index: i,

        quantity: element.quantity,

        price: element.sellPrice

      });

    });

    try {

      gtag('event', eventName, {

        coupon:
          orderObj?.promoCode || '',

        items: itemsArray

      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Cart Tracking
  // ============================================================
  public emitCartEventTracking(
    eventName: string,
    cartData: any
  ) {

    const itemsArray = [];

    let i = 0;

    cartData.skus.forEach(element => {

      i++;

      itemsArray.push({

        item_id: element.product.id,

        item_name: element.name,

        item_category:
          `${element.product.category || ''}/${element.product.subcategory || ''}`,

        item_variant: element.id,

        index: i,

        quantity: element.quantity,

        price: element.sellPrice

      });

    });

    try {

      gtag('event', eventName, {
        items: itemsArray
      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Checkout Step Tracking (GA4)
  // Replaces UA-only set_checkout_option / checkout_progress.
  // GA4 checkout funnel: begin_checkout → add_shipping_info → add_payment_info
  // Each event includes items so emitCheckoutEventTracking is not needed
  // alongside this call.
  // ============================================================
  public emitCheckoutOptions(
    routeData: any,
    orderData?: any
  ) {

    if (routeData instanceof NavigationCancel) {
      return;
    }

    const items = (orderData?.skus || []).map((element: any, i: number) => ({
      item_id: element.product?.id,
      item_name: element.product?.name,
      item_category: `${element.product?.category || ''}/${element.product?.subcategory || ''}`,
      item_variant: element.id,
      index: i + 1,
      quantity: element.quantity,
      price: element.sellPrice
    }));

    try {

      if (routeData.url.match('/checkout/info-shipping')) {

        gtag('event', 'begin_checkout', {
          currency: 'USD',
          value: orderData?.orderTotal || 0,
          coupon: orderData?.promoCode || '',
          items
        });

      } else if (routeData.url.match('/checkout/billing')) {

        gtag('event', 'add_shipping_info', {
          currency: 'USD',
          value: orderData?.orderTotal || 0,
          shipping_tier: orderData?.shipping?.method || '',
          items
        });

      } else if (routeData.url.match('/checkout/place-order')) {

        gtag('event', 'add_payment_info', {
          currency: 'USD',
          value: orderData?.orderTotal || 0,
          payment_type:
            orderData?.paymentMethod?.paymentTransaction?.type || '',
          items
        });

      }

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Select Content Tracking
  // ============================================================
  public emitSelectContentEventTracking(
    productData: any,
    skuData: any
  ) {

    const routeUrl =
      this.storageService.getItem('prevURL');

    const navigationExtra =
      this.storageService.getItem('navigationExtra');

    const itemsArray: any[] = [
      {
        item_id: productData.id,

        item_name: productData.name,

        item_brand:
          productData.brand?.name || '',

        item_category:
          `${productData.breadcrumbs?.category || ''}/${productData.breadcrumbs?.subcategory || ''}`
      }
    ];

    if (
      navigationExtra &&
      navigationExtra.indexOf('search') > -1
    ) {

      itemsArray[0].item_list_name =
        navigationExtra;

    } else if (routeUrl) {

      if (
        routeUrl.indexOf('/product-detail') > -1 &&
        navigationExtra
      ) {

        itemsArray[0].item_list_name =
          `PDP (${navigationExtra})`;

      } else if (
        routeUrl.indexOf('/product/brand') > -1
      ) {

        itemsArray[0].item_list_name =
          'brand page';

      } else if (
        routeUrl.indexOf('/product/') > -1
      ) {

        itemsArray[0].item_list_name =
          'PLP';

      } else if (
        routeUrl.indexOf('/useraccount/orders') > -1
      ) {

        itemsArray[0].item_list_name =
          'my orders';

      } else if (
        routeUrl.indexOf('/useraccount/wishlist-details') > -1 &&
        navigationExtra
      ) {

        itemsArray[0].item_list_name =
          `wishlist detail (${navigationExtra})`;

      } else if (
        routeUrl.indexOf('/useraccount/wishlist') > -1
      ) {

        itemsArray[0].item_list_name =
          'my wishlists';

      } else if (
        routeUrl.indexOf('/cart') > -1
      ) {

        itemsArray[0].item_list_name =
          'shopping cart';

      } else {

        itemsArray[0].item_list_name =
          'homepage';

      }

    } else {
      return;
    }

    try {

      gtag('event', 'select_item', {

        item_list_name: itemsArray[0]?.item_list_name || '',

        items: itemsArray

      });

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

  // ============================================================
  // Exception Tracking
  // ============================================================
  public emitExceptions(
    errorData: any,
    isFatal: boolean = false
  ) {

    try {

      gtag('event', 'exception', {
        description: String(errorData),
        fatal: isFatal
      });

    } catch (ex) { }

  }

  // ============================================================
  // Normalize a domain-like source value (from UTM params or
  // document.referrer) down to a bare engine name when it matches
  // a known search engine — strips protocol, "www.", and any
  // ccTLD/subdomain noise. Falls back to the original value
  // untouched for anything that isn't a known engine (partners,
  // affiliates, newsletters, etc.), so it's safe to run on every
  // incoming source value.
  //
  // Examples:
  //   "www.google.com"   -> "google"
  //   "m.google.de"       -> "google"
  //   "GOOGLE.CO.UK"      -> "google"
  //   "www.bing.com"      -> "bing"
  //   "mygoogleapp.com"   -> "mygoogleapp.com"  (not a token match, untouched)
  //   "newsletter"        -> "newsletter"       (untouched)
  // ============================================================
  private normalizeSourceValue(rawValue: string): string {

    if (!rawValue) {
      return rawValue;
    }

    const value = rawValue.toLowerCase().trim();

    // Strip protocol if a full URL was passed instead of a bare host
    const withoutProtocol = value.replace(/^https?:\/\//, '');

    // Strip path/query if present
    const hostOnly = withoutProtocol.split('/')[0];

    // Split into dot-separated labels so we match whole tokens,
    // not substrings (avoids false positives like "mygoogleapp.com")
    const labels = hostOnly.split('.');

    const matchedEngine =
      this.knownSearchEngines.find(engine =>
        labels.includes(engine)
      );

    return matchedEngine || rawValue;

  }

  // ============================================================
  // Capture UTM Parameters
  // ============================================================
  public captureUTMParameters(): void {

    if (!isPlatformBrowser(this.platform)) {
      return;
    }

    try {

      const urlParams =
        new URLSearchParams(window.location.search);

      const existingAttribution =
        this.storageService.sessionGetItem(
          'ga4_utm_params'
        );

      const utmParams: any = {};

      [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content'
      ].forEach(param => {

        const value = urlParams.get(param);

        if (value) {
          utmParams[param] = value;
        }

      });

      // Normalize source AFTER collecting raw params, so values like
      // utm_source=www.google.com / m.google.de / GOOGLE.CO.UK collapse
      // to "google" instead of leaking a raw hostname into GA4. This is
      // what fixes the "www.google.{tld} / {medium}" duplicate-bucket
      // issue. Any other known engine (bing, yahoo, etc.) benefits too.
      if (utmParams.utm_source) {
        utmParams.utm_source =
          this.normalizeSourceValue(utmParams.utm_source);
      }

      const gclid =
        urlParams.get('gclid');

      const gad_campaignid =
        urlParams.get('gad_campaignid');

      const gbraid =
        urlParams.get('gbraid');

      const wbraid =
        urlParams.get('wbraid');

      const isPaidClick =
        !!(gclid || gad_campaignid || gbraid || wbraid);

      // ======================================================
      // CASE 1: UTM Attribution
      // ======================================================
      if (Object.keys(utmParams).length > 0) {

        // Force all paid/advertising traffic to cpc (case-insensitive).
        // Covers gclid/gbraid/wbraid click IDs and any ad platform that
        // sets utm_medium=paid: YouTube, Reddit, DoubleClick, Gmail app, etc.
        const rawMedium = (utmParams.utm_medium || '').toLowerCase();
        if (isPaidClick || rawMedium === 'paid') {
          utmParams.utm_medium = 'cpc';
        }

        this.storageService.sessionSetItem(
          'ga4_utm_params',
          utmParams
        );

      }

      // ======================================================
      // CASE 2: Paid Click without UTM
      // ======================================================
      else if (isPaidClick) {

        this.storageService.sessionSetItem(
          'ga4_utm_params',
          {
            utm_source: 'google',
            utm_medium: 'cpc'
          }
        );

      }

      // ======================================================
      // CASE 3: Referrer Attribution
      // ======================================================
      else if (
        !existingAttribution &&
        document.referrer
      ) {

        try {

          const referrerUrl =
            new URL(document.referrer);

          const hostname =
            referrerUrl.hostname.toLowerCase();

          // Reuse the same normalizer used for UTM params so referrer-based
          // attribution (e.g. someone landing straight from a Google SERP
          // with no UTM params at all) also collapses www./subdomain
          // variants down to a bare engine name.
          const matchedEngine =
            this.normalizeSourceValue(hostname);

          const isKnownEngine =
            this.knownSearchEngines.includes(matchedEngine);

          this.storageService.sessionSetItem(
            'ga4_utm_params',
            {
              utm_source: matchedEngine,

              utm_medium:
                isKnownEngine
                  ? 'organic'
                  : 'referral'
            }
          );

        } catch (e) { }

      }

    } catch (ex) {
      this.emitExceptions(ex, false);
    }

  }

}